import clsx from 'clsx';
import { forwardRef, type KeyboardEvent, type MouseEvent } from 'react';

import { ChevronRightSmallIcon } from '@dynatrace/strato-icons';

import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { getSubTriggerSlots } from './item-slots.js';
import * as styles from './Menu.sty.js';
import { type MenuSubTriggerProps } from './SubTrigger.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

interface MobileSubTriggerProps extends MenuSubTriggerProps {
  onDrillDown: () => void;
}

/**
 * Visual clone of SubTrigger that calls onDrillDown on click instead of
 * opening a Radix popup. Used exclusively in mobile drill-down mode.
 * @internal
 */
export const MobileSubTrigger = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MobileSubTriggerProps
>(
  (
    {
      children,
      disabled,
      onDrillDown,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
    },
    forwardedRef,
  ) => {
    const slots = getSubTriggerSlots(children);
    const { focusProps, focusClassName } = useFocusRing({ disabled });
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();

    const handleClick = (e: MouseEvent) => {
      if (disabled) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      onDrillDown();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onDrillDown();
      }
    };

    return (
      <Flex
        ref={forwardedRef}
        role="menuitem"
        aria-disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        justifyContent="space-between"
        className={clsx(
          textStyleCSS({ textStyle: 'base-emphasized' }),
          !isItemHovered && focusClassName,
          styles.item({ disabled }),
        )}
        {..._mergeProps(focusProps, hoverProps, {
          onClick: handleClick,
          onKeyDown: handleKeyDown,
        })}
        tabIndex={disabled ? -1 : 0}
      >
        {slots.prefix}
        <div className={styles.subChildren}>{slots.freeform}</div>
        <ChevronRightSmallIcon />
      </Flex>
    );
  },
);

(
  MobileSubTrigger as typeof MobileSubTrigger & { displayName: string }
).displayName = 'Menu.SubTrigger';
